import React from "react";
import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "@mui/material";
import { LicenseInfo } from "@mui/x-license";
import App from "./App";
import { StyledToastContainer } from "./components/atoms";
import { AuthWrap, DialogManager } from "./components";
import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";
import "./index.css";
import { getLightTheme } from "./theme/light";
import { CSS_COLOR_VARIABLES } from "./constants/colors";

Object.entries(CSS_COLOR_VARIABLES).forEach(([property, value]) => {
  document.documentElement.style.setProperty(property, value);
});

LicenseInfo.setLicenseKey(import.meta.env.VITE_MUI_LICENSE_KEY);

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.VITE_ENVIRONMENT,
  release: import.meta.env.VITE_RELEASE,
  integrations: [
    Sentry.reactRouterBrowserTracingIntegration({
      // See docs for support of different versions of variation of react router
      // https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.captureConsoleIntegration(),
    Sentry.reportingObserverIntegration(),
    Sentry.extraErrorDataIntegration({
      depth: 10,
    }),
  ],
  tracesSampleRate: 1.0,
});

const queryClient = new QueryClient();

root.render(
  <AuthWrap>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={getLightTheme()}>
        <DialogManager>
          <App />
        </DialogManager>
      </ThemeProvider>
      <StyledToastContainer />
      <ReactQueryDevtools initialIsOpen={false} position="bottom" />
    </QueryClientProvider>
  </AuthWrap>
);
